import { useMutation } from '@apollo/client/react';
import { SheetDescription, SheetTitle } from '@optools/components/ui/sheet';
import {
  DELETE_VIEWING_POLICY,
  UPDATE_POLICY,
} from '@optools/services/operator-gateway/esni/mutations';
import { GET_VIEWING_POLICIES } from '@optools/services/operator-gateway/esni/queries';
import { ViewingPolicy } from '@optools/services/operator-gateway/esni/types';
import { ViewingPoliciesQuery } from '@optools/services/operator-gateway/types/generated/graphql';
import { useState } from 'react';
import { toast } from 'sonner';

import { PolicyForm, PolicyFormValues } from './policy-form';

export interface PolicyDetailProps {
  onOpenChange: ((open: boolean) => void) | undefined;
  viewingPolicy: ViewingPolicy | undefined;
}

export function PolicyDetail({ onOpenChange, viewingPolicy }: PolicyDetailProps) {
  const [editMode, setEditMode] = useState(false);

  const [updatePolicy, { loading: loadingUpdatePolicy }] = useMutation(UPDATE_POLICY, {
    onCompleted: ({ updatePolicy }) => {
      toast.success('Policy successfully updated', {
        description: `Updated ${updatePolicy?.name} policy`,
      });
      setEditMode(false);
    },
    update: (cache, { data }) => {
      if (!data?.updatePolicy) return;

      const query = cache.readQuery<ViewingPoliciesQuery>({
        query: GET_VIEWING_POLICIES,
      });

      if (!query) return;

      const updatedViewingPolicies = query?.viewingPolicies.map((policy) => {
        return policy.href === data?.updatePolicy?.href ? data.updatePolicy : policy;
      });

      cache.writeQuery<ViewingPoliciesQuery>({
        data: { viewingPolicies: updatedViewingPolicies },
        query: GET_VIEWING_POLICIES,
      });
    },
  });

  const [deletePolicy, { loading: loadingDeletePolicy }] = useMutation(DELETE_VIEWING_POLICY, {
    onCompleted: ({ deletePolicy }) => {
      const { href, reason, success } = deletePolicy;
      if (success) {
        toast.success('Deleted policy successfully', {
          description: `Deleted ${href} policy`,
        });
        onOpenChange?.(false);
      } else {
        toast.error('Failed to delete policy', {
          description: `Failed to delete policy ${href} due to: ${reason}`,
        });
      }
    },
    update: (cache, { data }) => {
      if (!data?.deletePolicy.success) return;

      const query = cache.readQuery<ViewingPoliciesQuery>({
        query: GET_VIEWING_POLICIES,
      });

      if (!query) return;

      const { href } = data.deletePolicy;

      cache.modify<ViewingPoliciesQuery>({
        fields: {
          viewingPolicies(existing, { readField }) {
            return existing.filter((item) => href !== readField('href', item));
          },
        },
      });
    },
  });

  const handleSubmit = (values: PolicyFormValues) => {
    const { href, ...policy } = values;
    updatePolicy({ variables: { href, policy: { ...policy, policyName: viewingPolicy?.name } } });
  };

  const handleDelete = (href: string) => {
    deletePolicy({ variables: { href } });
  };

  return (
    <>
      <SheetTitle className="mb-6">Viewing Policy</SheetTitle>
      <SheetDescription className="sr-only">Edit a viewing policy</SheetDescription>

      <div className="mb-4 space-y-1 text-sm">
        <p className="font-medium">Policy Name</p>
        <p className="opacity-50">{viewingPolicy?.name}</p>
      </div>

      <PolicyForm
        editMode={editMode}
        loading={loadingUpdatePolicy || loadingDeletePolicy}
        viewingPolicy={viewingPolicy}
        onCancel={() => setEditMode(false)}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        onToggleEdit={() => setEditMode(true)}
      />
    </>
  );
}
